import * as React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Icon, Label } from 'office-ui-fabric-react';
import ReactTable, { CellInfo, Column } from 'react-table';

import { ApiUrls, Permissions, PersonPlaningAutonomyType } from '@app/AppConstants';
import { PersonDestinationRestrictionModel, PersonGroupDestinationRestrictionModel, PersonPlaningAutonomyValidationResultModel, PersonDestinationRestrictionValidationResultModel, PersonGroupDestinationRestrictionValidationResultModel, PersonPlaningAutonomyViewModel } from '@app/Models';

import AirportStore from '@app/Stores/AirportStore';
import ApiService from '@app/Services/ApiService';
import InlineDateValidationService from '@app/Services/InlineDateValidationService';
import { securityService } from '@app/Services/SecurityService';

import { NewDestinationItem } from './Dialogs/NewDestinationItem';
import { NewDestinationGroup } from './Dialogs/NewDestinationGroup';
import { NotificationHandler } from '@app/Components/ToastNotification';
import { Loading } from '@app/Components/Loading';
import { TextCell } from '@app/Components/CellFormatters/TextCell';
import { InlineTextboxCellWithTooltip } from '@app/Components/CellFormatters/InlineTextboxCellWithTooltip';
import { NewPlaningAutonomyDialog } from './Dialogs/NewPlaningAutonomyDialog';
import { InlineDestinationGroupDropdownCell } from './InlineDestinationGroupDropdownCell';
import { InlineAirportsDropdownCell } from '@app/Pages/MasterData/Components/InlineAirportsDropdownCell';
import { InlineDateCell } from '@app/Components/CellFormatters/InlineDateCell';
import { InlinePlaningAutonomyDropdownCell } from '@app/Components/CellFormatters/InlinePlaningAutonomyDropdownCell';

import { PersonFleetTablesBlock } from '@app/Pages/PersonInfo/Components/PersonFleetTablesBlock';
import { modalService, ModalButtonType } from '@app/Components/Modal';
import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import { AzureAuth } from '@app/Components/AzureAuth';

type PlaningBlockProps = {
    personId: string;
    airportStore: AirportStore;
};

@observer
export class PlaningBlock extends React.Component<PlaningBlockProps, {}> {
    private _store: PlaningBlockStore;
    
    constructor(props: PlaningBlockProps) {
        super(props);
        this._store = new PlaningBlockStore(props.personId);    
    }

    componentDidUpdate(prevProps: Readonly<PlaningBlockProps>){
        if(prevProps.personId !== this.props.personId){
            this._store.personId = this.props.personId;
            this._store.loadDestinationRestriction(this._store.personId);
            this._store.loadDestinationRestrictionGroup(this._store.personId);
            this._store.loadPersonPlaningAutonomy(this._store.personId);
        }
    }
    
    render() {
        const {personId} = this.props;
        const {personPlaningAutonomy, personDestinationRestriction, personDestinationRestrictionGroup} = this._store;
        if (!personId) return;
        const { hasPermission, permissionCss } = securityService;

        const canSeePlanningAutonomy = hasPermission(Permissions.ViewObjectPersonPlanningAutonomy);
        const canSeeDestinationRestriction = hasPermission(Permissions.ViewObjectPersonDestinationRestriction);
        const canSeeDestinationRestrictionGroup = hasPermission(Permissions.ViewObjectPersonDestinationRestrictionGroup);
        const canSeePersonFleet = hasPermission(Permissions.ViewObjectPersonFleet, true);

        if (!canSeePlanningAutonomy && !canSeeDestinationRestriction && !canSeeDestinationRestrictionGroup && !canSeePersonFleet){
            return null;
        }

        return (
            <fieldset className="person-info-planning">
                <legend>Planning</legend>
                <div className="person-info-block">
                    {canSeePlanningAutonomy &&
                        <>
	                    <Loading loading={this._store.personPlaningAutonomyLoader.isPending} />
	                    <div className="table-label">
	                        <Label>Planning Autonomy</Label>
	                        {hasPermission(Permissions.ViewAddObjectPersonPlanningAutonomy, !AzureAuth.isReadOnly) && <button className={'table-label-button' + permissionCss(Permissions.ViewObjectPersonPlanningAutonomy, Permissions.EditObjectPersonPlanningAutonomy)} onClick={this._addPlanningAutonomy}>
	                            <Icon iconName="Add" className="ms-IconTable" />
	                        </button>}
	                    </div>
	                    {personPlaningAutonomy && <ReactTable<PersonPlaningAutonomyViewModel>
	                        data={personPlaningAutonomy}
	                        columns={this._planningAutonomyColumns}
	                        pageSize={personPlaningAutonomy.length}
	                        showPageSizeOptions={false}
	                        showPagination={false}
	                        className={'ReactTable-fixed-height' + permissionCss(Permissions.ViewObjectPersonPlanningAutonomy, Permissions.EditObjectPersonPlanningAutonomy)}
	                    />}
                        </>
                    }
                    {canSeeDestinationRestriction &&
                        <>
                            <Loading loading={this._store.destinationRestrictionLoader.isPending} />
                            <div className="table-label">
                                <Label>Destination Restriction</Label>
                                {hasPermission(Permissions.ViewAddObjectPersonDestinationRestriction, !AzureAuth.isReadOnly) && <button className={'table-label-button ' + permissionCss(Permissions.ViewObjectPersonDestinationRestriction, Permissions.EditObjectPersonDestinationRestriction)} onClick={this._addDestinationRestriction}>
                                    <Icon iconName="Add" className="ms-IconTable" />
                                </button>}
                            </div>
                            {personDestinationRestriction && <ReactTable<PersonDestinationRestrictionModel>
                                data={personDestinationRestriction}
                                columns={this._destinationRestrictionColumns}
                                pageSize={personDestinationRestriction.length}
                                showPageSizeOptions={false}
                                showPagination={false}
                                className={'ReactTable-fixed-height' + permissionCss(Permissions.ViewObjectPersonDestinationRestriction, Permissions.EditObjectPersonDestinationRestriction)}
                            />}
                        </>
                    }
                    {canSeeDestinationRestrictionGroup && 
                        <>
	                    <Loading loading={this._store.destinationRestrictionGroupLoader.isPending} />
	                    <div className="table-label">
	                        <Label>Destination Restriction Group</Label>
	                        {hasPermission(Permissions.ViewAddObjectPersonDestinationRestrictionGroup, !AzureAuth.isReadOnly) && <button className={'table-label-button ' + permissionCss(Permissions.ViewObjectPersonDestinationRestriction, Permissions.EditObjectPersonDestinationRestriction)} onClick={this._addDestinationRestrictionGroup}>
	                            <Icon iconName="Add" className="ms-IconTable" />
	                        </button>}
	                    </div>
	                    {personDestinationRestrictionGroup && <ReactTable<PersonGroupDestinationRestrictionModel>
	                        data={personDestinationRestrictionGroup}
	                        columns={this._destinationRestrictionGroupColumns}
	                        pageSize={personDestinationRestrictionGroup.length}
	                        showPageSizeOptions={false}
	                        showPagination={false}
	                        className={'ReactTable-fixed-height' + permissionCss(Permissions.ViewObjectPersonDestinationRestriction, Permissions.EditObjectPersonDestinationRestriction)}
	                    />}
                        </>
                    }
                    {canSeePersonFleet &&
                        <PersonFleetTablesBlock personId={personId}/>
                    }
                </div>
            </fieldset>
        );
    }

    @action.bound
    private _onPlaningAutonomyColumnChange(params: PersonPlaningAutonomyViewModel) {
        return this._store.updatePlaningAutonomy(params);
    }

    @action.bound
    private async _addPlanningAutonomy() {
        const { button } = await modalService.show(NewPlaningAutonomyDialog, {
            personId: this.props.personId
        });

        if (button === ModalButtonType.Save){
            await this._store.loadPersonPlaningAutonomy(this.props.personId);
        }
    }

    @action.bound
    private _renderPlanningAutonomyDateFrom(cellInfo: CellInfo){
        return <InlineDateCell<PersonPlaningAutonomyViewModel>
            onValidate={InlineDateValidationService.validateFromDate} 
            value={cellInfo.value} 
            onSelect={this._onPlaningAutonomyColumnChange} 
            row={cellInfo.row._original}
            fieldKey={cellInfo.column.id as keyof PersonPlaningAutonomyViewModel} 
            isReadOnly={!securityService.hasPermission(Permissions.EditPlanningAutonomyDateFrom, !AzureAuth.isReadOnly)}
        />;
    }
    
    @action.bound
    private _renderPlanningAutonomyDateTo(cellInfo: CellInfo){
        return <InlineDateCell<PersonPlaningAutonomyViewModel>
            onValidate={InlineDateValidationService.validateDate} 
            value={cellInfo.value as keyof PersonPlaningAutonomyType} 
            onSelect={this._onPlaningAutonomyColumnChange} 
            row={cellInfo.row._original}
            fieldKey={cellInfo.column.id as keyof PersonPlaningAutonomyViewModel}
            isReadOnly={!securityService.hasPermission(Permissions.EditPlanningAutonomyDateTo, !AzureAuth.isReadOnly)}
        />;
    }

    @action.bound
    private _renderPlanningAutonomyType(cellInfo: CellInfo){
        return <InlinePlaningAutonomyDropdownCell 
            value={cellInfo.value} 
            onChange={this._onPlaningAutonomyColumnChange} 
            row={cellInfo.row._original}
            disabled={!securityService.hasPermission(Permissions.EditPlanningAutonomyType, !AzureAuth.isReadOnly)}
        />;
    }
    
    @action.bound
    private _renderDeletePlanningAutonomyColumn(cellInfo: CellInfo){
        return <i className="ms-Icon ms-Icon-action ms-Icon--Delete" aria-hidden="true" onClick={async () => this._onDeletePlaningAutonomy(cellInfo)} />;
    }

    private async _onDeletePlaningAutonomy(cellInfo: CellInfo) {
        await modalService.showConfirmation('Are you sure you want to delete this planning autonomy?', 'Warning', {
            onConfirm: async () => {
                await this._store.deletePlaningAutonomy(cellInfo.value);
            }
        });
    }

    @action.bound
    private async _addDestinationRestriction() {
        const { button } = await modalService.show(NewDestinationItem, {
            airportOptions: this.props.airportStore.airportSearchableOptions,
            personId: this.props.personId
        });

        if (button === ModalButtonType.Save){
            await this._store.loadDestinationRestriction(this.props.personId);
        }
    }

    @action.bound
    private _renderDestinationRestrictionAirportType(cellInfo: CellInfo){
        return <InlineAirportsDropdownCell<PersonDestinationRestrictionModel>
            airportStore={this.props.airportStore} 
            cellInfo={cellInfo} 
            value={cellInfo.value} 
            onChange={this._onDestinationRestrictionColumnChange} 
            row={cellInfo.row._original} 
            fieldKey="airportCode"
            isReadOnly={!securityService.hasPermission(Permissions.EditPersonDestinationRestrictionAirportCode, !AzureAuth.isReadOnly)}
        />;
    }
    
    private _renderDestinationRestrictionAirportCode = (cellInfo: CellInfo) => {
        return <TextCell value={cellInfo.value} />;
    };

    private _renderDestinationRestrictionDateFromCell = (accessor: keyof PersonDestinationRestrictionModel, canBeEmpty?: boolean) => (cellInfo: CellInfo) => {
        return <InlineDateCell<PersonDestinationRestrictionModel>
            onValidate={InlineDateValidationService.validateFromDate} 
            value={cellInfo.value} 
            onSelect={this._onDestinationRestrictionColumnChange} 
            row={cellInfo.row._original}
            fieldKey={accessor} 
            canBeEmpty={canBeEmpty}
            isReadOnly={!securityService.hasPermission(Permissions.EditPersonDestinationRestrictionDateFrom, !AzureAuth.isReadOnly)}
        />;
    };

    private _renderDestinationRestrictionDateToCell = (accessor: keyof PersonDestinationRestrictionModel, canBeEmpty?: boolean) => (cellInfo: CellInfo) => {
        return <InlineDateCell<PersonDestinationRestrictionModel>
            onValidate={InlineDateValidationService.validateDate} 
            value={cellInfo.value} 
            onSelect={this._onDestinationRestrictionColumnChange} 
            row={cellInfo.row._original} 
            fieldKey={accessor} 
            canBeEmpty={canBeEmpty}
            isReadOnly={!securityService.hasPermission(Permissions.EditPersonDestinationRestrictionDateTo, !AzureAuth.isReadOnly)}
        />;
    };

    private _renderDestinationRestrictionCommentCell = (accessor: keyof PersonDestinationRestrictionModel, isRequired?: boolean) => (cellInfo: CellInfo) => {
        return <InlineTextboxCellWithTooltip<PersonDestinationRestrictionModel>
            cellInfo={cellInfo} 
            value={cellInfo.value}
            onChange={this._onDestinationRestrictionColumnChange} 
            row={cellInfo.row._original} 
            required={isRequired} 
            fieldKey={accessor}
            disabled={!securityService.hasPermission(Permissions.EditPersonDestinationRestrictionComment, !AzureAuth.isReadOnly)}
        />;
    };

    @action.bound
    private _renderDeleteDestinationRestrictionColumn(cellInfo: CellInfo){
        return <i className="ms-Icon ms-Icon-action ms-Icon--Delete" aria-hidden="true" onClick={async () => this._onDeleteDestinationRestrictionClick(cellInfo)} />;
    }

    private async _onDeleteDestinationRestrictionClick(cellInfo: CellInfo) {
        await modalService.showConfirmation('Are you sure you want to delete this destination restriction?', 'Warning', {
            onConfirm: async () => {
                await this._store.deleteDestinationRestriction(cellInfo.value);
            }
        });
    }

    @action.bound
    private _onDestinationRestrictionColumnChange(params: PersonDestinationRestrictionModel) {
        this._store.updateDestinationRestriction(params);
    }
    
    @action.bound
    private async _addDestinationRestrictionGroup() {
        const { button } = await modalService.show(NewDestinationGroup, {
            personId: this.props.personId
        });

        if (button === ModalButtonType.Save){
            await this._store.loadDestinationRestrictionGroup(this.props.personId);
        }
    }

    private _renderGroupDateFromCell = (accessor: keyof PersonGroupDestinationRestrictionModel, canBeEmpty?: boolean) => (cellInfo: CellInfo) => {
        return <InlineDateCell<PersonGroupDestinationRestrictionModel>
            onValidate={InlineDateValidationService.validateFromDate} 
            value={cellInfo.value}
            onSelect={this._onGroupChange} 
            row={cellInfo.row._original}
            fieldKey={accessor}
            canBeEmpty={canBeEmpty} 
            isReadOnly={!securityService.hasPermission(Permissions.EditPersonGroupDestinationRestrictionDateFrom, !AzureAuth.isReadOnly)}
        />;
    };
    
    private _renderGroupDateToCell = (accessor: keyof PersonGroupDestinationRestrictionModel, canBeEmpty?: boolean) => (cellInfo: CellInfo) => {
        return <InlineDateCell<PersonGroupDestinationRestrictionModel>
            onValidate={InlineDateValidationService.validateDate} 
            value={cellInfo.value}
            onSelect={this._onGroupChange}
            row={cellInfo.row._original} 
            fieldKey={accessor} 
            canBeEmpty={canBeEmpty}
            isReadOnly={!securityService.hasPermission(Permissions.EditPersonGroupDestinationRestrictionDateTo, !AzureAuth.isReadOnly)}
        />;
    };
    
    private _renderGroupNameCell = (cellInfo: CellInfo) => {
        return <InlineDestinationGroupDropdownCell 
            value={cellInfo.value} 
            onChange={this._onGroupChange} 
            row={cellInfo.row._original} 
            isReadOnly={!securityService.hasPermission(Permissions.EditObjectPersonDestinationRestrictionGroup, !AzureAuth.isReadOnly)}
            loader={this._store.destinationRestrictionLoader}
        />;
    };
    
    @action.bound
    private _onGroupChange(params: PersonGroupDestinationRestrictionModel){
        this._store.updateDestinationRestrictionGroup(params);
    }
    
    private _renderGroupCommentCell = (accessor: keyof PersonGroupDestinationRestrictionModel, isRequired?: boolean) => (cellInfo: CellInfo) => {
        return <InlineTextboxCellWithTooltip<PersonGroupDestinationRestrictionModel>
            cellInfo={cellInfo} 
            value={cellInfo.value} 
            onChange={this._onGroupChange} 
            row={cellInfo.row._original} 
            fieldKey={accessor}
            required={isRequired}
            disabled={!securityService.hasPermission(Permissions.EditPersonGroupDestinationRestrictionComment, !AzureAuth.isReadOnly)}
        />;
    };

    @action.bound
    private _renderDeleteDestinationRestrictionGroupColumn = (cellInfo: CellInfo) => {
        return <i className="ms-Icon ms-Icon-action ms-Icon--Delete" aria-hidden="true" onClick={async () => this._onDeleteDestinationRestrictionGroupClick(cellInfo)} />;
    };
    
    private async _onDeleteDestinationRestrictionGroupClick(cellInfo: CellInfo) {
        await modalService.showConfirmation('Are you sure you want to delete this destination restriction group?', 'Warning', {
            onConfirm: async () => {
                await this._store.deleteDestinationRestrictionGroup(cellInfo.value);
            }
        });
    }

    private _planningAutonomyColumns: Column<PersonPlaningAutonomyViewModel>[] = [
        {
            Header: 'From',
            accessor: 'dateFrom',
            Cell: this._renderPlanningAutonomyDateFrom,
            show: securityService.hasPermission(Permissions.ViewPlanningAutonomyDateFrom),
            className: securityService.permissionCss(Permissions.ViewPlanningAutonomyDateFrom, Permissions.EditPlanningAutonomyDateFrom)
        },
        {
            Header: 'To',
            accessor: 'dateTo',
            Cell: this._renderPlanningAutonomyDateTo,
            show: securityService.hasPermission(Permissions.ViewPlanningAutonomyDateTo),
            className: securityService.permissionCss(Permissions.ViewPlanningAutonomyDateTo, Permissions.EditPlanningAutonomyDateTo)
        },
        {
            Header: 'Autonomy Type',
            accessor: 'autonomyType',
            Cell: this._renderPlanningAutonomyType,
            show: securityService.hasPermission(Permissions.ViewPlanningAutonomyType),
            className: securityService.permissionCss(Permissions.ViewPlanningAutonomyType, Permissions.EditPlanningAutonomyType)
        },
        {
            Header: '',
            accessor: 'id',
            sortable: false,
            Cell: this._renderDeletePlanningAutonomyColumn,
            show: securityService.hasPermission(Permissions.EditObjectPersonPlanningAutonomy, !AzureAuth.isReadOnly),
            maxWidth: 50,
            className: securityService.permissionCss(Permissions.ViewObjectPersonPlanningAutonomy, Permissions.EditObjectPersonPlanningAutonomy)
        }
    ];

    private _destinationRestrictionColumns: Column<PersonDestinationRestrictionModel>[] = [
        {
            Header: 'Airport Code',
            accessor: 'airportCode',
            Cell: this._renderDestinationRestrictionAirportCode
        },
        {
            Header: 'Airport',
            accessor: 'airportCode',
            Cell: this._renderDestinationRestrictionAirportType,
            show: securityService.hasPermission(Permissions.ViewPersonDestinationRestrictionAirportCode),
            className: securityService.permissionCss(Permissions.ViewPersonDestinationRestrictionAirportCode, Permissions.EditPersonDestinationRestrictionAirportCode)
        },
        {
            Header: 'From',
            accessor: 'dateFrom',
            Cell: this._renderDestinationRestrictionDateFromCell('dateFrom'),
            show: securityService.hasPermission(Permissions.ViewPersonDestinationRestrictionDateFrom),
            className: securityService.permissionCss(Permissions.ViewPersonDestinationRestrictionDateFrom, Permissions.EditPersonDestinationRestrictionDateFrom)
        },
        {
            Header: 'To',
            accessor: 'dateTo',
            Cell: this._renderDestinationRestrictionDateToCell('dateTo', true),
            show: securityService.hasPermission(Permissions.ViewPersonDestinationRestrictionDateTo),
            className: securityService.permissionCss(Permissions.ViewPersonDestinationRestrictionDateTo, Permissions.EditPersonDestinationRestrictionDateTo)
        },
        {
            Header: 'Comment',
            accessor: 'comment',
            Cell: this._renderDestinationRestrictionCommentCell('comment'),
            show: securityService.hasPermission(Permissions.ViewPersonDestinationRestrictionComment),
            className: securityService.permissionCss(Permissions.ViewPersonDestinationRestrictionComment, Permissions.EditPersonDestinationRestrictionComment)
        },
        {
            Header: '',
            accessor: 'id',
            sortable: false,
            maxWidth: 50,
            Cell: this._renderDeleteDestinationRestrictionColumn,
            className: securityService.permissionCss(Permissions.ViewObjectPersonDestinationRestriction, Permissions.EditObjectPersonDestinationRestriction),
            show: securityService.hasPermission(Permissions.EditObjectPersonDestinationRestriction, !AzureAuth.isReadOnly) 
        }
    ];
    
    private _destinationRestrictionGroupColumns: Column<PersonGroupDestinationRestrictionModel>[] = [
        {
            Header: 'Group Name',
            accessor: 'destinationRestrictionGroupId',
            Cell: this._renderGroupNameCell,
            show: securityService.hasPermission(Permissions.ViewObjectPersonDestinationRestrictionGroup),
            className: securityService.permissionCss(Permissions.ViewObjectPersonDestinationRestrictionGroup, Permissions.EditObjectPersonDestinationRestrictionGroup)
        },
        {
            Header: 'From',
            accessor: 'dateFrom',
            Cell: this._renderGroupDateFromCell('dateFrom', true),
            show: securityService.hasPermission(Permissions.ViewPersonGroupDestinationRestrictionDateFrom),
            className: securityService.permissionCss(Permissions.ViewPersonGroupDestinationRestrictionDateFrom, Permissions.EditPersonGroupDestinationRestrictionDateFrom)
        },
        {
            Header: 'To',
            accessor: 'dateTo',
            Cell: this._renderGroupDateToCell('dateTo'),
            show: securityService.hasPermission(Permissions.ViewPersonGroupDestinationRestrictionDateTo),
            className: securityService.permissionCss(Permissions.ViewPersonGroupDestinationRestrictionDateTo, Permissions.EditPersonGroupDestinationRestrictionDateTo)
        },
        {
            Header: 'Comment',
            accessor: 'comment',
            Cell: this._renderGroupCommentCell('comment'),
            show: securityService.hasPermission(Permissions.ViewPersonGroupDestinationRestrictionComment),
            className: securityService.permissionCss(Permissions.ViewPersonGroupDestinationRestrictionComment, Permissions.EditPersonGroupDestinationRestrictionComment)
        },
        {
            Header: '',
            accessor: 'id',
            sortable: false,
            maxWidth: 50,
            Cell: this._renderDeleteDestinationRestrictionGroupColumn,
            className: securityService.permissionCss(Permissions.ViewObjectPersonDestinationRestriction, Permissions.EditObjectPersonDestinationRestriction),
            show: securityService.hasPermission(Permissions.EditObjectPersonDestinationRestriction, !AzureAuth.isReadOnly)
        }
    ];
}

class PlaningBlockStore {
    @observable personPlaningAutonomy: PersonPlaningAutonomyViewModel[];
    @observable personDestinationRestriction: PersonDestinationRestrictionModel[];
    @observable personDestinationRestrictionGroup: PersonGroupDestinationRestrictionModel[];
    @observable personId: string;
    public personPlaningAutonomyLoader: PromiseCompletion = new PromiseCompletion();
    public destinationRestrictionLoader: PromiseCompletion = new PromiseCompletion();
    public destinationRestrictionGroupLoader: PromiseCompletion = new PromiseCompletion();

    constructor(personId: string){
        this.personId = personId;
        this.loadPersonPlaningAutonomy(personId);
        this.loadDestinationRestriction(personId);
        this.loadDestinationRestrictionGroup(personId);
    }

    @action.bound
    async loadPersonPlaningAutonomy(selectedPersonId: string) {
        if(!securityService.hasPermission(Permissions.ViewObjectPersonPlanningAutonomy))
            return ;
        const params = { personId: selectedPersonId };
        const { data: planingAutonomy } = await ApiService.getTypedData<PersonPlaningAutonomyViewModel[]>(ApiUrls.PersonPlaningAutonomyUrl, params, { completion: this.personPlaningAutonomyLoader });
        this.personPlaningAutonomy = planingAutonomy;
    }

    @action.bound
    async loadDestinationRestriction(personId: string) {
        if (!securityService.hasPermission(Permissions.ViewObjectPersonDestinationRestriction))
            return;

        const params = { personId: personId };
        const { data: restrictions } = await ApiService.getTypedData<PersonDestinationRestrictionModel[]>(ApiUrls.PersonDestinationRestrictionUrl, params);
        this.personDestinationRestriction = restrictions;
    }

    @action.bound
    async loadDestinationRestrictionGroup(personId: string) {
        if(!securityService.hasPermission(Permissions.ViewObjectPersonDestinationRestrictionGroup))
            return;
        const params = { personId: personId };
        const { data: restrictionGroups } = await ApiService.getData(ApiUrls.PersonDestinationRestrictionGroupUrl, params, { completion: this.destinationRestrictionGroupLoader });
        this.personDestinationRestrictionGroup = restrictionGroups;
    }
    
    @action.bound
    async updatePlaningAutonomy(params: PersonPlaningAutonomyViewModel) {
        const { data: result } = await ApiService.putTypedData<PersonPlaningAutonomyValidationResultModel>(ApiUrls.PersonPlaningAutonomyUrl, params);

        if (result.hasErrors && result.errors) {
            await modalService.showInformation(result.errors.join('\r\n '));
        } else {
            NotificationHandler.showSuccess('Person Planing Autonomy was updated', 'Success message');
        }

        const updatedPlaningAutonomy = result.value as PersonPlaningAutonomyViewModel;
        if (updatedPlaningAutonomy) {
            this.personPlaningAutonomy = this.personPlaningAutonomy.map(item => item.id === updatedPlaningAutonomy.id ? updatedPlaningAutonomy : item);
        }
    }
    
    @action.bound
    async updateDestinationRestriction(params: PersonDestinationRestrictionModel) {
        const { data: result } = await ApiService.putTypedData<PersonDestinationRestrictionValidationResultModel>(ApiUrls.PersonDestinationRestrictionUrl, params);

        if (result.hasErrors && result.errors) {
            await modalService.showInformation(result.errors.join('\r\n '));
        } else {
            NotificationHandler.showSuccess('Person Destination Restriction was updated', 'Success message');
        }

        const updatedItem = result.value as PersonDestinationRestrictionModel;
        if (updatedItem) {
            this.personDestinationRestriction = this.personDestinationRestriction.map((item: PersonDestinationRestrictionModel) => item.id === updatedItem.id ? updatedItem : item);
        }
    }

    @action.bound
    async updateDestinationRestrictionGroup(params: PersonGroupDestinationRestrictionModel) {
        const { data: result } = await ApiService.putTypedData<PersonGroupDestinationRestrictionValidationResultModel>(ApiUrls.PersonDestinationRestrictionGroupUrl, params);

        if (result.hasErrors && result.errors) {
            await modalService.showInformation(result.errors.join('\r\n '));
        } else {
            NotificationHandler.showSuccess('Person Destination Restriction was updated', 'Success message');
        }

        const updateGroup = result.value as PersonGroupDestinationRestrictionModel;
        if (updateGroup) {
            this.personDestinationRestrictionGroup = this.personDestinationRestrictionGroup.map((item: PersonGroupDestinationRestrictionModel) => item.id === updateGroup.id ? updateGroup : item);
        }
    }

    @action.bound
    async deletePlaningAutonomy(id: string) {
        await ApiService.deleteData(ApiUrls.PersonPlaningAutonomyUrl + '?personPlaningAutonomyId=' + id);
        NotificationHandler.showSuccess('Person Planing Autonomy was deleted', 'Success message');
        if (this.personId) {
            await this.loadPersonPlaningAutonomy(this.personId);
        }
    }

    @action.bound
    async deleteDestinationRestriction(personDestinationRestrictionId: string) {
        await ApiService.deleteData(ApiUrls.PersonDestinationRestrictionUrl + '?personDestinationRestrictionId=' + personDestinationRestrictionId);
        NotificationHandler.showSuccess('Destination Restriction was deleted', 'Success message');
        if (this.personId) {
            await this.loadDestinationRestriction(this.personId);
        }
    }

    @action.bound
    async deleteDestinationRestrictionGroup(personDestinationRestrictionId: string) {
        await ApiService.deleteData(ApiUrls.PersonDestinationRestrictionGroupUrl + '?id=' + personDestinationRestrictionId);
        NotificationHandler.showSuccess('Destination Restriction was deleted', 'Success message');
        if (this.personId) {
            await this.loadDestinationRestrictionGroup(this.personId);
        }
    }
}